import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { isAbortError, toZwApiError, type ZwApiError } from '../api/client';
import type { BookingPublic, ManageResponse, Slot } from '../api/types';
import { useSlots } from '../hooks/use-slots';
import { baseLanguage, labelForErrorCode, labelForPolicy, type Labels } from '../i18n/labels';
import { ZeitwahlProvider, useZeitwahl } from '../provider';
import {
  addDays,
  dateIn,
  monthOf,
  monthRange,
  todayIn,
  type ISODate,
  type ISOMonth,
} from '../time/calendar';
import { formatInstantDateTime, formatMinutes } from '../time/format';
import { BookingDetails, SelectedTime } from './booking-details';
import { MonthCalendar } from './month-calendar';
import { Chevron, Notice, RetryButton } from './notice';
import { SlotPicker } from './slot-picker';
import { TimeZoneSelect } from './time-zone-select';

export interface ManageBookingProps {
  /** Inherits from an enclosing `ZeitwahlProvider` when omitted. */
  apiBaseUrl?: string;
  /** The `manage` token from the visitor's email / confirmation link. */
  token: string;
  locale?: string;
  labels?: Partial<Labels>;
  widgetKey?: string;
  /** Display zone; defaults to the zone the visitor booked in. */
  timezone?: string;
  onRescheduled?: (booking: BookingPublic, context: { manageUrl: string }) => void;
  onCancelled?: (booking: BookingPublic) => void;
  /** When given (and no `renderLanguageToggle`), a built-in EN/DE toggle calls it. */
  onLocaleChange?: (locale: string) => void;
  /** Host-rendered toggle so the page can keep `?token` in its links. */
  renderLanguageToggle?: (context: { locale: string; token: string }) => ReactNode;
  onError?: (error: ZwApiError) => void;
  fetch?: typeof fetch;
  headers?: Record<string, string>;
  className?: string;
}

type View = 'details' | 'reschedule' | 'cancel' | 'rescheduled' | 'cancelled';

interface ManageNotice {
  text: string;
  nextAvailable?: string;
  retryAfterSeconds?: number;
}

/** Loads `GET /v1/bookings/{token}` and offers reschedule and cancel within the event type's policies. */
export function ManageBooking(props: ManageBookingProps): ReactNode {
  return (
    <ZeitwahlProvider
      apiBaseUrl={props.apiBaseUrl}
      widgetKey={props.widgetKey}
      locale={props.locale}
      labels={props.labels}
      timezone={props.timezone}
      fetch={props.fetch}
      headers={props.headers}
    >
      <ManageBookingInner {...props} />
    </ZeitwahlProvider>
  );
}

function ManageBookingInner(props: ManageBookingProps): ReactNode {
  const { token, onRescheduled, onCancelled, onLocaleChange, renderLanguageToggle, onError } =
    props;
  const { api, locale, labels, t, timeZone: zoneOverride } = useZeitwahl();
  const reasonId = useId();

  const [data, setData] = useState<ManageResponse | null>(null);
  const [loadError, setLoadError] = useState<ZwApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [view, setView] = useState<View>('details');
  const [timeZone, setTimeZone] = useState<string | null>(zoneOverride ?? null);
  const [month, setMonth] = useState<ISOMonth | null>(null);
  const [selectedDate, setSelectedDate] = useState<ISODate | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<ManageNotice | null>(null);
  const [noticeSeq, setNoticeSeq] = useState(0);
  const [result, setResult] = useState<{ booking: BookingPublic; manageUrl?: string } | null>(null);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api.getManage(token, { signal: controller.signal }).then(
      (res) => {
        if (controller.signal.aborted) return;
        setData(res);
        setLoadError(null);
        setLoading(false);
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        const err = toZwApiError(error);
        setLoadError(err);
        setLoading(false);
        onErrorRef.current?.(err);
      }
    );
    return () => controller.abort();
  }, [api, token, version]);

  const tz = timeZone ?? data?.booking.timeZone ?? 'UTC';
  const today = todayIn(tz);
  const currentMonth = monthOf(today);
  const visibleMonth = month && month >= currentMonth ? month : currentMonth;
  const range = monthRange(visibleMonth);
  const slots = useSlots({
    org: data?.eventType.org.slug ?? '',
    eventType: data?.eventType.slug ?? '',
    from: range.from,
    to: range.to,
    tz,
    reschedule: token,
    enabled: view === 'reschedule' && !!data,
  });
  const maxMonth = data ? monthOf(addDays(today, data.eventType.bookingWindowDays)) : undefined;

  const showNotice = (n: ManageNotice) => {
    setNotice(n);
    setNoticeSeq((s) => s + 1);
  };

  const handleError = (error: unknown) => {
    const err = toZwApiError(error);
    onError?.(err);
    const d = err.details;
    switch (err.code) {
      case 'slot_unavailable': {
        slots.refresh();
        setSlot(null);
        const next = typeof d.nextAvailable === 'string' ? d.nextAvailable : undefined;
        showNotice({
          text: next
            ? t('errorSlotUnavailableNext', { time: formatInstantDateTime(next, locale, tz) })
            : t('errorSlotUnavailable'),
          nextAvailable: next,
        });
        break;
      }
      case 'policy_violation':
        showNotice({
          text: labelForPolicy(labels, d.policy ?? '', {
            notice: formatMinutes(
              typeof d.minNoticeMinutes === 'number' ? d.minNoticeMinutes : 0,
              locale
            ),
          }),
        });
        break;
      case 'booking_not_active':
      case 'invalid_token':
      case 'not_found':
        setLoadError(err);
        setView('details');
        break;
      default: {
        const seconds = typeof d.retryAfterSeconds === 'number' ? d.retryAfterSeconds : 30;
        showNotice({
          text: labelForErrorCode(labels, err.code, { seconds }),
          retryAfterSeconds:
            err.code === 'rate_limited' || err.code === 'provider_unavailable'
              ? seconds
              : undefined,
        });
      }
    }
  };

  const startReschedule = () => {
    if (!data) return;
    const bookedDate = dateIn(tz, data.booking.start);
    setMonth(monthOf(bookedDate) > currentMonth ? monthOf(bookedDate) : currentMonth);
    setSelectedDate(bookedDate >= today ? bookedDate : null);
    setSlot(null);
    setNotice(null);
    setView('reschedule');
  };

  const confirmReschedule = async () => {
    if (!slot) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.reschedule(token, {
        start: slot.start,
        timeZone: tz,
        reason: reason.trim() || undefined,
      });
      setResult(res);
      setView('rescheduled');
      onRescheduled?.(res.booking, { manageUrl: res.manageUrl });
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.cancel(token, { reason: reason.trim() || undefined });
      setResult({ booking: res.booking });
      setView('cancelled');
      onCancelled?.(res.booking);
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  const languageToggle = renderLanguageToggle
    ? renderLanguageToggle({ locale, token })
    : onLocaleChange && (
        <div className="zw-lang" role="group" aria-label={t('languageLabel')}>
          {(['en', 'de'] as const).map((lang) => (
            <button
              key={lang}
              type="button"
              className="zw-lang-btn"
              aria-pressed={baseLanguage(locale) === lang}
              onClick={() => onLocaleChange(lang)}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
      );

  const noticeView = notice && (
    <Notice kind="error">
      <p>{notice.text}</p>
      {notice.nextAvailable && (
        <button
          type="button"
          className="zw-button zw-button--secondary"
          onClick={() => {
            const date = dateIn(tz, notice.nextAvailable!);
            setMonth(monthOf(date));
            setSelectedDate(date);
            setNotice(null);
          }}
        >
          {t('errorSlotUnavailableShow')}
        </button>
      )}
      {notice.retryAfterSeconds !== undefined && (
        <RetryButton
          key={noticeSeq}
          seconds={notice.retryAfterSeconds}
          onRetry={() => setNotice(null)}
        />
      )}
    </Notice>
  );

  const renderLoadError = (err: ZwApiError): ReactNode => {
    const superseded = err.details.supersededBy;
    if (err.code === 'booking_not_active' && superseded) {
      return (
        <Notice kind="warning">
          <p>{t('errorBookingSuperseded', { ref: superseded.publicRef })}</p>
          <a className="zw-button" href={superseded.manageUrl}>
            {t('errorBookingSupersededLink')}
          </a>
        </Notice>
      );
    }
    if (err.code === 'booking_not_active')
      return <Notice kind="warning">{t('errorBookingNotActive')}</Notice>;
    if (err.code === 'invalid_token' || err.code === 'not_found') {
      return <Notice kind="error">{t('errorInvalidToken')}</Notice>;
    }
    return (
      <Notice kind="error">
        <p>{labelForErrorCode(labels, err.code)}</p>
        <RetryButton onRetry={() => setVersion((v) => v + 1)} />
      </Notice>
    );
  };

  const reasonField = (label: string) => (
    <div className="zw-field">
      <label className="zw-label" htmlFor={reasonId}>
        {label}
        <span className="zw-muted zw-optional"> ({t('optional')})</span>
      </label>
      <textarea
        id={reasonId}
        className="zw-input zw-textarea"
        value={reason}
        maxLength={1000}
        onChange={(e) => setReason(e.target.value)}
      />
    </div>
  );

  return (
    <div
      className={`zw-root zw-manage zw-manage--${view}${props.className ? ` ${props.className}` : ''}`}
    >
      <header className="zw-manage-head">
        <h2 className="zw-title">{t('manageTitle')}</h2>
        {languageToggle}
      </header>

      {loading && (
        <p className="zw-loading" role="status">
          {t('loading')}
        </p>
      )}
      {!loading && loadError && renderLoadError(loadError)}

      {!loading && !loadError && data && view === 'details' && (
        <>
          <BookingDetails booking={data.booking} timeZone={tz} locale={locale} showStatus />
          {noticeView}
          <div className="zw-actions">
            {data.actions.reschedule && (
              <button type="button" className="zw-button" onClick={startReschedule}>
                {t('manageReschedule')}
              </button>
            )}
            {data.actions.cancel && (
              <button
                type="button"
                className="zw-button zw-button--secondary"
                onClick={() => {
                  setNotice(null);
                  setView('cancel');
                }}
              >
                {t('manageCancel')}
              </button>
            )}
          </div>
          {!data.actions.reschedule && data.actions.reasons?.reschedule && (
            <p className="zw-muted">
              {labelForPolicy(labels, data.actions.reasons.reschedule, {
                notice: formatMinutes(data.eventType.policies.rescheduleMinNoticeMinutes, locale),
              })}
            </p>
          )}
          {!data.actions.cancel && data.actions.reasons?.cancel && (
            <p className="zw-muted">
              {labelForPolicy(labels, data.actions.reasons.cancel, {
                notice: formatMinutes(data.eventType.policies.cancelMinNoticeMinutes, locale),
              })}
            </p>
          )}
        </>
      )}

      {!loading && !loadError && data && view === 'reschedule' && (
        <>
          <button type="button" className="zw-back" onClick={() => setView('details')}>
            <Chevron direction="left" />
            {t('back')}
          </button>
          <h3 className="zw-subtitle">{t('manageRescheduleTitle')}</h3>
          {noticeView}
          {slots.degraded.length > 0 && <Notice kind="warning">{t('slotsDegraded')}</Notice>}
          {slots.error && (
            <Notice kind="error">
              <p>{labelForErrorCode(labels, slots.error.code)}</p>
              <RetryButton onRetry={slots.refresh} />
            </Notice>
          )}
          <div className="zw-pick">
            <div className="zw-pick-calendar">
              <MonthCalendar
                month={visibleMonth}
                onMonthChange={(m) => {
                  setMonth(m);
                  if (selectedDate && monthOf(selectedDate) !== m) setSelectedDate(null);
                }}
                selected={selectedDate}
                onSelect={setSelectedDate}
                availableDates={slots.availableDates}
                loading={slots.loading}
                minMonth={currentMonth}
                maxMonth={maxMonth}
                today={today}
                locale={locale}
              />
              <TimeZoneSelect value={tz} onChange={setTimeZone} locale={locale} />
            </div>
            <SlotPicker
              date={selectedDate}
              slots={selectedDate ? (slots.slotsByDate.get(selectedDate) ?? []) : []}
              timeZone={tz}
              locale={locale}
              selected={slot}
              onSelect={setSlot}
              loading={slots.loading}
            />
          </div>
          {slot && (
            <div className="zw-reschedule-confirm">
              <p className="zw-label">{t('manageNewTime')}</p>
              <SelectedTime slot={slot} timeZone={tz} locale={locale} />
              {reasonField(t('manageRescheduleReason'))}
              <div className="zw-actions">
                <button
                  type="button"
                  className="zw-button"
                  disabled={busy}
                  onClick={() => void confirmReschedule()}
                >
                  {t('manageRescheduleConfirm')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {!loading && !loadError && data && view === 'cancel' && (
        <div className="zw-cancel">
          <h3 className="zw-subtitle">{t('manageCancelTitle')}</h3>
          <BookingDetails booking={data.booking} timeZone={tz} locale={locale} />
          {noticeView}
          {reasonField(t('manageCancelReason'))}
          <div className="zw-actions">
            <button
              type="button"
              className="zw-button zw-button--danger"
              disabled={busy}
              onClick={() => void confirmCancel()}
            >
              {t('manageCancelConfirm')}
            </button>
            <button
              type="button"
              className="zw-button zw-button--secondary"
              onClick={() => setView('details')}
            >
              {t('manageKeep')}
            </button>
          </div>
        </div>
      )}

      {view === 'rescheduled' && result && (
        <>
          <Notice kind="success">{t('manageRescheduled')}</Notice>
          <BookingDetails booking={result.booking} timeZone={tz} locale={locale} showStatus />
          {result.manageUrl && (
            <p>
              <a className="zw-link" href={result.manageUrl}>
                {t('manageOpenCurrent')}
              </a>
            </p>
          )}
        </>
      )}

      {view === 'cancelled' && result && (
        <>
          <Notice kind="success">{t('manageCancelled')}</Notice>
          <BookingDetails booking={result.booking} timeZone={tz} locale={locale} showStatus />
        </>
      )}
    </div>
  );
}
